import type { Room, Booking, BookingStatus } from '@/types/booking';
import React, { useState } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarIcon, Clock, Building2, StickyNote } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency } from '@/lib/utils';
import { ToggleWithLabel } from '@/components/ui/toggle-with-label';
import { useStaff } from '@/contexts/StaffContext';
import { useBookings } from '@/contexts/BookingContext';
import { DuplicateBookingAlert } from './DuplicateBookingAlert';
import { staffConfig } from '@/config/staff';
import { ConflictWarningDialog } from './ConflictWarningDialog';
import { validateBookingTime } from '@/lib/booking-utils';
import { useToast } from '@/hooks/use-toast';

type BookingDialogProps = {
  room: Room;
  date: Date;
  hour: number;
  onClose: () => void;
  onSubmit: (booking: Booking) => void;
};

const statusStyles = {
  paid: 'px-3 py-1.5 w-[200px] bg-blue-100 border-l-4 border-blue-500 text-blue-800 hover:bg-blue-200/80 transition-all duration-200',
  booked: 'px-3 py-1.5 w-[200px] bg-emerald-100 border-l-4 border-emerald-500 text-emerald-800 hover:bg-emerald-200/80 transition-all duration-200',
  pending: 'px-3 py-1.5 w-[200px] bg-amber-100 border-l-4 border-amber-500 text-amber-800 hover:bg-amber-200/80 transition-all duration-200'
};

export function BookingDialog({ room, date, hour, onClose, onSubmit }: BookingDialogProps) {
  const { selectedStaff } = useStaff();
  const { checkDuplicate } = useBookings();
  const { toast } = useToast();
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictBooking, setConflictBooking] = useState<Booking | null>(null);

  const [form, setForm] = useState({
    bookedBy: selectedStaff?.name || '',
    clientName: '',
    duration: '1',
    status: 'pending' as BookingStatus,
    isFullDay: false,
    amount: selectedStaff ? staffConfig.find(s => s.name === selectedStaff.name)?.defaults.amount || 0 : 0,
    hasNotes: false,
    notes: '',
    timeSlot: `${hour.toString().padStart(2, '0')}:00`
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newBooking: Booking = {
      id: Date.now(),
      roomId: room.id,
      date: format(date, 'yyyy-MM-dd'),
      timeSlot: form.isFullDay ? '09:00' : form.timeSlot,
      duration: form.isFullDay ? 14 : parseInt(form.duration),
      bookedBy: form.bookedBy,
      clientName: form.bookedBy === 'SuHe' ? 'SuHe' : form.clientName,
      status: form.status,
      revenue: {
        amount: form.bookedBy === 'SuHe' ? 0 : form.amount,
        hasNotes: form.hasNotes,
        notes: form.hasNotes ? form.notes : undefined
      }
    };

    // Validate booking time
    const timeError = validateBookingTime(newBooking);
    if (timeError) {
      toast({
        title: "Invalid Time",
        description: timeError,
        variant: "destructive"
      });
      return;
    }

    // Check for conflicts
    const conflict = checkDuplicate(newBooking);
    if (conflict) {
      setConflictBooking(newBooking);
      setShowConflictWarning(true);
      return;
    }

    onSubmit(newBooking);
    onClose();
  };

  const handleAlternativeSelect = (newDate: string, newTime: string) => {
    if (conflictBooking) {
      const updatedBooking = {
        ...conflictBooking,
        date: newDate,
        timeSlot: newTime
      };
      onSubmit(updatedBooking);
      onClose();
    }
  };

  const handleStaffSelect = (staffName: string) => {
    const staffDefaults = staffConfig.find(s => s.name === staffName)?.defaults;
    setForm(prev => ({
      ...prev,
      bookedBy: staffName,
      clientName: staffName === 'SuHe' ? 'SuHe' : '',
      amount: staffDefaults?.amount || 0,
      status: staffDefaults?.status || 'pending'
    }));
  };

  const handleDurationChange = (value: string) => {
    if (value === 'full') {
      setForm(prev => ({ ...prev, duration: '14', isFullDay: true }));
    } else {
      setForm(prev => ({ ...prev, duration: value, isFullDay: false }));
    }
  };

  const isSuHeSelected = form.bookedBy === 'SuHe';
  const isFormValid = form.bookedBy && (isSuHeSelected || form.clientName);

  return (
    <>
      <Dialog open modal onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>New Booking</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CalendarIcon className="h-4 w-4" />
                <span>{format(date, 'PPP')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                <span>
                  {form.isFullDay ? (
                    <span className="font-medium text-purple-600">Full Day (9:00-23:00)</span>
                  ) : (
                    `Start Time: ${hour.toString().padStart(2, '0')}:00`
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Building2 className="h-4 w-4" />
                <span>{room.name}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bookedBy">Booked By</Label>
              <Select 
                value={form.bookedBy}
                onValueChange={handleStaffSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staffConfig.map(staff => (
                    <SelectItem key={staff.id} value={staff.name}>
                      <span className={cn("px-2 py-1 rounded-md", staff.color)}>
                        {staff.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientName">Client Company</Label>
              <Input
                id="clientName"
                value={isSuHeSelected ? 'SuHe' : form.clientName}
                onChange={(e) => setForm({...form, clientName: e.target.value})}
                disabled={isSuHeSelected}
                placeholder={form.bookedBy ? "Enter client name" : "Select staff member first"}
                className="hover:border-gray-400 focus:border-purple-500 transition-colors"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Duration</Label>
              <Select 
                value={form.isFullDay ? 'full' : form.duration}
                onValueChange={handleDurationChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full" className="font-medium text-purple-600">
                    Full Day (9:00-23:00)
                  </SelectItem>
                  <SelectItem value="1">1 hour</SelectItem>
                  {Array.from({ length: 7 }, (_, i) => (
                    <SelectItem key={i + 2} value={(i + 2).toString()}>
                      {i + 2} hours
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select 
                value={form.status}
                onValueChange={(value) => setForm({...form, status: value as BookingStatus})}
                disabled={isSuHeSelected}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">
                    <div className={cn("px-3 py-1 rounded-md", statusStyles.paid)}>
                      Paid
                    </div>
                  </SelectItem>
                  <SelectItem value="booked">
                    <div className={cn("px-3 py-1 rounded-md", statusStyles.booked)}>
                      Booked
                    </div>
                  </SelectItem>
                  <SelectItem value="pending">
                    <div className={cn("px-3 py-1 rounded-md", statusStyles.pending)}>
                      Pending
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Amount (€)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  €
                </span>
                <Input
                  id="amount"
                  type="text"
                  inputMode="decimal"
                  pattern="^\d*\.?\d{0,2}$"
                  className="pl-7 hover:border-gray-400 focus:border-purple-500 transition-colors"
                  value={isSuHeSelected ? '0' : form.amount.toString()}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    const parts = value.split('.');
                    if (parts.length > 2) return;
                    if (parts[1]?.length > 2) return;
                    
                    setForm(prev => ({
                      ...prev,
                      amount: value === '' ? 0 : parseFloat(value) || 0
                    }));
                  }}
                  disabled={isSuHeSelected}
                  style={{ appearance: 'textfield' }}
                />
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <ToggleWithLabel
                id="notes-enabled"
                checked={form.hasNotes}
                onChange={(checked) => 
                  setForm(prev => ({
                    ...prev,
                    hasNotes: checked
                  }))
                }
                label="Add Notes"
                leftIcon={<StickyNote className="h-4 w-4" />}
              />

              {form.hasNotes && (
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add any notes..."
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      notes: e.target.value
                    }))}
                    className="hover:border-gray-400 focus:border-purple-500 transition-colors"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 pt-2">
              <Button 
                type="submit" 
                className={cn(
                  "w-full transition-all duration-200",
                  isFormValid
                    ? "bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                )}
                disabled={!isFormValid}
              >
                Book Room
              </Button>
              <Button 
                type="button" 
                onClick={onClose}
                className={cn(
                  "w-full bg-white text-red-600",
                  "border-2 border-red-500",
                  "transition-all duration-200",
                  "hover:bg-red-50 active:bg-red-100",
                  "hover:border-red-600"
                )}
                aria-label="Cancel booking creation"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {showConflictWarning && conflictBooking && (
        <ConflictWarningDialog
          conflict={checkDuplicate(conflictBooking)!}
          rooms={[room]}
          open={showConflictWarning}
          onClose={() => setShowConflictWarning(false)}
          onSelectAlternative={handleAlternativeSelect}
        />
      )}
    </>
  );
}